import { Capacitor, registerPlugin } from '@capacitor/core'

// Shared bridge for the native "activity" plugins (LiveActivity, SyncActivity):
// a small state machine so a disabled/unsupported native side never spams the
// console nor triggers pointless native calls. A failing activity must NEVER
// break the feature it decorates (recording, sync).
//
// Native rejects carry machine codes (UNSUPPORTED / DISABLED / NO_ACTIVE); the
// message fallbacks keep older native builds classified correctly.

const states = {
    idle: 'idle',
    starting: 'starting',
    active: 'active',
    disabled: 'disabled',
    unsupported: 'unsupported',
}

const getErrorMessage = (error) => String(error?.message || error?.errorMessage || error || '')

const isDisabledError = (error) =>
    error?.code === 'DISABLED' || getErrorMessage(error).includes('Live Activities are not enabled')
const isUnsupportedError = (error) => error?.code === 'UNSUPPORTED' || getErrorMessage(error).includes('unsupported')
const isNoActiveError = (error) =>
    error?.code === 'NO_ACTIVE' || getErrorMessage(error).includes('No active Live Activity')

export default function createNativeActivityBridge(pluginName, { stopMethod = 'end' } = {}) {
    const native = registerPlugin(pluginName)
    const tag = `[${pluginName}]`

    let state = states.idle
    const infoLogged = new Set()

    const logInfoOnce = (key, message) => {
        if (infoLogged.has(key)) return
        infoLogged.add(key)
        console.info(message)
    }

    const isAvailable = () => Capacitor.isNativePlatform() && Capacitor.isPluginAvailable(pluginName)

    // Whether the OS currently allows the activity (iOS: user setting; Android: always true).
    const isSupported = async () => {
        if (!isAvailable()) return false

        try {
            const { supported } = await native.isSupported()
            return !!supported
        } catch {
            return false
        }
    }

    const canStart = async () => {
        if (!isAvailable()) {
            state = states.unsupported
            return false
        }

        if (await isSupported()) {
            state = states.idle
            return true
        }

        state = states.disabled
        logInfoOnce('unavailable', `${tag} unavailable or disabled by user; native updates skipped`)
        return false
    }

    const start = async (data) => {
        if (!(await canStart())) return null

        try {
            state = states.starting
            const result = await native.start(data)
            state = states.active
            // Some platforms resolve without a payload; callers only need truthiness.
            return result ?? true
        } catch (error) {
            if (isDisabledError(error)) {
                state = states.disabled
                logInfoOnce('disabled', `${tag} disabled by user; native updates skipped`)
                return null
            }

            if (isUnsupportedError(error)) {
                state = states.unsupported
                logInfoOnce('unsupported', `${tag} unsupported; native updates skipped`)
                return null
            }

            state = states.idle
            console.warn(`${tag} start failed`, error)
            return null
        }
    }

    const update = async (data) => {
        if (!isAvailable() || state !== states.active) return

        try {
            await native.update(data)
        } catch (error) {
            if (isNoActiveError(error)) {
                state = states.idle
                logInfoOnce('missing', `${tag} no active native activity; updates skipped until next start`)
                return
            }

            if (isDisabledError(error)) {
                state = states.disabled
                logInfoOnce('disabled', `${tag} disabled by user; native updates skipped`)
                return
            }

            console.warn(`${tag} update failed`, error)
        }
    }

    const stop = async () => {
        if (!isAvailable()) return
        state = states.idle

        try {
            await native[stopMethod]()
        } catch (error) {
            console.warn(`${tag} ${stopMethod} failed`, error)
        }
    }

    return {
        isAvailable,
        isSupported,
        start,
        update,
        [stopMethod]: stop,
    }
}
